import React, { useEffect, useState } from 'react';
import {
  List,
  ListItem,
  ListItemText,
  Collapse,
  Box,
  Tooltip,
  IconButton,
  TextField,
} from '@mui/material';
import { Close, ExpandLess, ExpandMore, Search } from '@mui/icons-material';
import destinationIcon from './images/destination.png';
import salesForce from './images/salesforce.png';
import oracle from './images/oracle.png';
import sql from './images/sql.png';
import hubspot from './images/hubspot.png';
import github from './images/github.png';
import PropTypes from 'prop-types';
import { motion } from 'framer-motion';

const listVars = {
  hidden: { opacity: 0 },
  show: {
    opacity: 1,
    transition: { staggerChildren: 0.07, delayChildren: 0.1 },
  },
};
const itemVars = {
  hidden: { y: 8, opacity: 0 },
  show: { y: 0, opacity: 1, transition: { duration: 0.25, ease: 'easeOut' } },
};

const destinations = [
  { name: 'Salesforce', sourceType: 'Salesforce', icon: salesForce },
  { name: 'SQL', sourceType: 'SQL', icon: sql },
  { name: 'Oracle', sourceType: 'Oracle', icon: oracle },
  { name: 'HubSpot', sourceType: 'HubSpot', icon: hubspot },
  { name: 'GitHub', sourceType: 'GitHub', icon: github },
];

const DestinationPanel = ({ clearTrigger }) => {
  const [selectedItems, setSelectedItems] = useState([]);
  const [openDestination, setOpenDestination] = useState(true);

  const [showSearch, setShowSearch] = useState(false);
  const [q, setQ] = useState('');
  const filtered = destinations.filter((d) =>
    d.name.toLowerCase().includes(q.toLowerCase()),
  );

  useEffect(() => {
    if (clearTrigger) setSelectedItems([]);
  }, [clearTrigger]);

  const handleItemClick = (e, itemName) => {
    if (e.ctrlKey || e.metaKey || e.shiftKey) {
      setSelectedItems((prev) =>
        prev.includes(itemName)
          ? prev.filter((n) => n !== itemName)
          : [...prev, itemName],
      );
    } else {
      setSelectedItems([itemName]);
    }
  };

  const handleDragStart = (e, draggedItem) => {
    const baseItems =
      selectedItems.length > 1
        ? destinations.filter((d) => selectedItems.includes(d.name))
        : [draggedItem];

    const dragItems = baseItems.map((item) => ({
      ...item,
      label: `${item.name} (Destination)`,
      type: 'Destination',
      config: {
        ...item,
        label: `${item.name} (Destination)`,
        type: 'destinationNode',
      },
    }));

    if (dragItems.length === 1) {
      e.dataTransfer.setData('application/reactflow', dragItems[0].name);
    }
    e.dataTransfer.setData('multi-nodes', JSON.stringify(dragItems));
    e.dataTransfer.setData('nodeType', 'Destination');
    e.dataTransfer.effectAllowed = 'move';
  };

  const toggleDestination = () => {
    if (openDestination) {
      setShowSearch(false);
      setQ('');
    }

    setOpenDestination(!openDestination);
  };

  return (
    <List disablePadding>
      <ListItem
        button
        onClick={toggleDestination}
        sx={{
          background: openDestination ? '#525252' : 'transparent',
          color: openDestination ? '#fff' : '#000',
          borderTopLeftRadius: '4px',
          borderTopRightRadius: '4px',
          px: 2,
          py: 1,
          fontWeight: 600,
          fontSize: 16,
          mb: 1,
          letterSpacing: 1,
          transition: 'background 0.2s, color 0.2s',
          display: 'flex',
          alignItems: 'center',
          cursor: 'pointer',
          paddingLeft: '5px',
          '&:hover': {
            background: openDestination ? '#4c4c4cff' : 'rgba(90, 102, 166, 0.1)',
            color: openDestination ? '#fff' : '#000',
          },
        }}
      >
        <img
          src={destinationIcon}
          alt="destination"
          width={25}
          height={25}
          style={{
            marginRight: 15,
            filter: openDestination ? 'brightness(0) invert(1)' : 'none',
            transition: 'filter 0.2s',
            padding: '4px',
          }}
        />
        <motion.div
          initial={{ y: -8, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          transition={{ duration: 0.25, ease: 'easeOut' }}
        >
          <ListItemText
            primaryTypographyProps={{ fontSize: 13, fontWeight: 500 }}
            primary="Destination"
          />
        </motion.div>

        <Box
          sx={{ display: 'flex', alignItems: 'center', ml: 'auto', gap: 0.5 }}
        >
          {openDestination && (
            <IconButton
              size="small"
              onClick={(e) => {
                e.stopPropagation();
                setShowSearch((x) => !x);
              }}
              sx={{ mr: 0.5, color: '#fff' }}
            >
              {showSearch ? (
                <Close fontSize="small" />
              ) : (
                <Search fontSize="small" />
              )}
            </IconButton>
          )}

          {openDestination ? <ExpandLess /> : <ExpandMore />}
        </Box>
      </ListItem>

      <Collapse in={openDestination} timeout="auto" unmountOnExit>
        <motion.div
          initial={{ x: -20, opacity: 0 }}
          animate={{ x: 0, opacity: 1 }}
          transition={{ duration: 0.35, ease: 'easeOut', delay: 0.12 }}
        >
          <Box
            sx={{
              background: '#F7F9FF',
              pb: 1,
              borderBottomLeftRadius: 8,
              borderBottomRightRadius: 8,
            }}
          >
            {showSearch && (
              <Box sx={{ px: 2, pt: 1 }}>
                <TextField
                  size="small"
                  fullWidth
                  placeholder="Search destinations"
                  value={q}
                  onChange={(e) => setQ(e.target.value)}
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      borderRadius: '6px',
                      height: 32,
                      fontSize: 13,
                      px: 1,
                    },
                  }}
                  InputProps={{ sx: { color: '#000' } }}
                />
              </Box>
            )}

            <List
              component={motion.ul}
              variants={listVars}
              initial="hidden"
              animate="show"
              disablePadding
            >
              {filtered.map((item, idx) => (
                <ListItem
                  key={`${item.name}-${idx}`}
                  component={motion.li}
                  variants={itemVars}
                  selected={selectedItems.includes(item.name)}
                  onClick={(e) => handleItemClick(e, item.name)}
                  sx={{
                    pl: 4,
                    py: 0.5,
                    cursor: 'pointer',
                    backgroundColor: selectedItems.includes(item.name)
                      ? 'rgba(0, 86, 210, 0.15)'
                      : 'transparent',
                    '&:hover': {
                      backgroundColor: 'rgba(0, 86, 210, 0.08)',
                      borderRadius: '4px',
                      transition: 'background-color 0.2s',
                    },
                  }}
                  draggable
                  onDragStart={(e) => handleDragStart(e, item)}
                >
                  <Tooltip
                    title={`Drag to add ${item.name} destination`}
                    placement="right"
                  >
                    <Box
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        width: '100%',
                      }}
                    >
                      <img
                        src={
                          item.name === 'Salesforce' ? salesForce : item.icon
                        }
                        alt={`${item.name} destination`}
                        width={item.name === 'Salesforce' ? 35 : 25}
                        height={25}
                        style={{
                          marginRight: item.name === 'Salesforce' ? 10 : 20,
                        }}
                      />
                      <ListItemText
                        primaryTypographyProps={{
                          fontSize: 12,
                          fontWeight: 500,
                        }}
                        primary={item.name}
                      />
                    </Box>
                  </Tooltip>
                </ListItem>
              ))}
            </List>
          </Box>
        </motion.div>
      </Collapse>
    </List>
  );
};

DestinationPanel.propTypes = {
  clearTrigger: PropTypes.number,
};

export default DestinationPanel;
